import { logAndSetStoreValue } from './logAndSetStoreValue'
import type {
  TEntityMaskItem,
  TEntityId,
  TPartialEntityData,
  TEntityData,
} from '~/types/models'

import { useEntityDialogStore } from '~/stores/entityDialog'

// -----

/**
 * What a pre-processor hands back. saveEntityData AWAITS it
 * (`newEntityData = await preRequestProcessor(newEntityData)`), and what comes
 * back may be the entity to send, an ARRAY (a batch PATCH -- see editEntity),
 * or a transaction payload. These interfaces used to say a bare synchronous
 * TEntityData, which every async processor in the app contradicts -- invisibly,
 * while the components passing them had `this` collapsed to `never`.
 */
export type TPreProcessorResult =
  | TEntityData
  | TEntityData[]
  | Record<string, unknown>

// `fieldData` is `any`, deliberately: each processor is written for ITS entity
// (`(newEntityData: IContract) => …`), and a call signature's parameter is
// checked contravariantly, so TPartialEntityData here would reject them all.
export interface IEntityPreCreateProcessorFunction {
  (fieldData: any): TPreProcessorResult | Promise<TPreProcessorResult>
}

export interface IEntityPreSaveUpdateProcessorFunction {
  (fieldData: any): TPreProcessorResult | Promise<TPreProcessorResult>
}

export type TEntityPreProcessorFunction =
  | IEntityPreCreateProcessorFunction
  | IEntityPreSaveUpdateProcessorFunction

// -----

/**
 * An after-request callback. saveEntityData calls it with an OBJECT --
 * `{ newEntityData }`, `{ updatedEntityData }` or `{ deletedEntityData }`
 * (runAfterRequestCallback) -- never with the entity itself, which is what these
 * types used to claim. Every real callback destructures one of those keys. The
 * argument is `any` for the same contravariance reason as the processors: each
 * callback names its own entity type.
 */
export interface IEntityCreatedOrDeletedCallback {
  (arg: any): unknown
}

export interface IEntityUpdatedCallback {
  (arg: any): unknown
}

export type TEntityCallback =
  | IEntityCreatedOrDeletedCallback
  | IEntityUpdatedCallback

// -----

export interface ISubentityAfterRequestCallback {
  entityName: string
  callback: Function
}

export interface ISetShowEntityDialog {
  callingMn: string
  entityName: string
  inputFieldsEntityNameOverride?: string | undefined // Will only have a value if we want to use other than the 'real' entityName to get the inputFields
  inputFieldsOverride?: TEntityMaskItem[] // To support custom field sets
  entityIdOverride?: TEntityId // Used to supply entity Id for fieldSet manipulations, where entityId is not in entityData
  entityData?: TEntityData // Will have a value if we're updating or deleting, but not creating
  entityParentObjectForCreate?: any // Will have a value if we're creating a child entity
  desiredAction: 'CREATE' | 'EDIT' | 'DELETE'
  preRequestProcessor?: TEntityPreProcessorFunction | undefined // Will not have a value for a DELETE.
  // Optional: callers pass a component prop that legitimately defaults to
  // undefined (createEditDeleteEntity's createdCallback / updatedCallback /
  // deletedCallback), and the body already guards with `if (afterRequestCallback)`.
  // Declaring it required made every one of those call sites a TS2322.
  afterRequestCallback?: TEntityCallback | undefined
  subentityNameTitleOverride?: string | null
  subentityAfterRequestCallbacks?: ISubentityAfterRequestCallback[] | undefined
  subentityParentObjectForCreate?: Function | undefined // DON'T THINK THIS SHOULD BE A FUNCTION. JR. Wil only have a value if subentity is itself child (e.g. adding a new companyContact to locationCompanyContacts)
  storeEditedInputDataAsJsonInProperty?: string
  titlePrefixOverride?: string | null
  titleOverride?: string | null
  subtitle?: string | null
  message?: string | null
  saveButtonCaptionVerbOverride?: string | null
  deleteButtonCaptionsVerbOverride?: string | null
  allowAddAnother?: boolean
  startInViewMode?: boolean
  disableEditButton?: boolean
  suppressFocusOnFirstField?: boolean
  // Show `message` as a quieter instruction (EntityMaskForm's prop).
  doSubdueMessage?: boolean
  // Line `message` up with the title and fields (EntityMaskForm's prop).
  doAlignMessageWithTitle?: boolean
}

export async function setShowEntityDialog(obj: ISetShowEntityDialog) {
  const {
    callingMn,
    entityName,
    inputFieldsEntityNameOverride,
    inputFieldsOverride,
    entityIdOverride,
    entityData,
    entityParentObjectForCreate,
    desiredAction,
    preRequestProcessor,
    afterRequestCallback,
    subentityNameTitleOverride,
    subentityAfterRequestCallbacks,
    subentityParentObjectForCreate,
    storeEditedInputDataAsJsonInProperty,

    titlePrefixOverride,
    titleOverride,
    subtitle,
    message,
    saveButtonCaptionVerbOverride,
    deleteButtonCaptionsVerbOverride,
    allowAddAnother,
    startInViewMode,
    disableEditButton,
    suppressFocusOnFirstField,
    doSubdueMessage,
    doAlignMessageWithTitle,
  } = obj

  const fn = `${callingMn} --> [cynqueModules/setShowEntityDialog.ts] setShowEntityDialog |`

  // Belt and suspenders - Clear everything.
  // Some or all properties may still be set after a broswer refresh.
  console.debug(
    `${fn} Clearing EntityDialogStore and EntitySubDialogStore properties...`
  )
  await useEntityDialogStore().clearEntityAndSubentityDialogs()

  // Set stuff
  //
  // These values are immediately visible to EntityDialog and EntityMaskForm
  // when set in the store.
  // If we set entityData before desiredAction, EntityMaskForm tries to
  // initialize for data entry instead of data delete, and throws a type error
  // because entityData for deleting might be an array, and entityData for
  // entry is only allowed to be object, not an array.
  //
  if (desiredAction) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'desiredAction',
      value: desiredAction,
    })
  }

  if (entityName) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityName',
      value: entityName,
    })
  }

  if (inputFieldsEntityNameOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'overrideEntityNameForInputFields',
      value: inputFieldsEntityNameOverride,
    })
  }

  if (inputFieldsOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'inputFieldsOverride',
      value: inputFieldsOverride,
    })
  }

  if (entityIdOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityIdOverride',
      value: entityIdOverride,
    })
  }

  if (entityData) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityData',
      value: entityData,
    })
  }

  if (entityParentObjectForCreate) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityParentObjectForCreate',
      value: entityParentObjectForCreate,
    })
  }

  if (preRequestProcessor) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'preRequestProcessor',
      value: preRequestProcessor,
    })
  }

  if (afterRequestCallback) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'afterRequestCallback',
      value: afterRequestCallback,
    })
  }

  if (subentityNameTitleOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'subentityNameTitleOverride',
      value: subentityNameTitleOverride,
    })
  }

  if (subentityAfterRequestCallbacks) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'subentityAfterRequestCallbacks',
      value: subentityAfterRequestCallbacks,
    })
  }

  if (subentityParentObjectForCreate) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'subentityParentObjectForCreate',
      value: subentityParentObjectForCreate,
    })
  }

  if (storeEditedInputDataAsJsonInProperty) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'storeEditedSpecsDataAsJsonInProperty',
      value: storeEditedInputDataAsJsonInProperty,
    })
  }

  if (titlePrefixOverride || titlePrefixOverride === '') {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityNameTitlePrefixOverride',
      value: titlePrefixOverride,
    })
  }

  if (titleOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'entityNameTitleOverride',
      value: titleOverride,
    })
  }

  if (subtitle) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'subtitle',
      value: subtitle,
    })
  }

  if (message) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'message',
      value: message,
    })
  }

  if (saveButtonCaptionVerbOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'saveButtonCaptionVerbOverride',
      value: saveButtonCaptionVerbOverride,
    })
  }

  if (deleteButtonCaptionsVerbOverride) {
    await logAndSetStoreValue({
      fn,
      storePropertyName: 'deleteButtonCaptionsVerbOverride',
      value: deleteButtonCaptionsVerbOverride,
    })
  }

  await logAndSetStoreValue({
    fn,
    storePropertyName: 'allowAddAnother',
    value: allowAddAnother || false,
  })

  await logAndSetStoreValue({
    fn,
    storePropertyName: 'startInViewMode',
    value: startInViewMode || false,
  })

  await logAndSetStoreValue({
    fn,
    storePropertyName: 'disableEditButton',
    value: disableEditButton || false,
  })

  await logAndSetStoreValue({
    fn,
    storePropertyName: 'suppressFocusOnFirstField',
    value: suppressFocusOnFirstField || false,
  })

  await logAndSetStoreValue({
    fn,
    storePropertyName: 'doSubdueMessage',
    value: doSubdueMessage || false,
  })
  await logAndSetStoreValue({
    fn,
    storePropertyName: 'doAlignMessageWithTitle',
    value: doAlignMessageWithTitle || false,
  })

  // All done setting stuff. Show the dialog.
  console.debug(`${fn}  Setting showEntityDialog to: true`)
  await useEntityDialogStore().setShowEntityDialog(true)
}

// //
// if (desiredAction) {
//   console.debug(`${fn}  Setting desiredAction`)
//   await store.dispatch(`entityDialog/setDesiredAction`, desiredAction, {
//     root: true,
//   })
// }

// console.debug(`${fn}  Setting entityName to: ${entityName}`)
// await store.dispatch(`entityDialog/setEntityName`, entityName, {
//   root: true,
// })

// if (inputFieldsEntityNameOverride) {
//   console.debug(`${fn}  Setting inputFieldsEntityNameOverride`)
//   await store.dispatch(
//     `entityDialog/setOverrideEntityNameForInputFields`,
//     inputFieldsEntityNameOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (inputFieldsOverride) {
//   console.debug(`${fn}  Setting inputFieldsOverride`)
//   await store.dispatch(
//     `entityDialog/setInputFieldsOverride`,
//     inputFieldsOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (entityIdOverride) {
//   console.debug(`${fn}  Setting entityIdOverride`)
//   await store.dispatch(`entityDialog/setEntityIdOverride`, entityIdOverride, {
//     root: true,
//   })
// }

// if (entityData) {
//   console.debug(`${fn}  Setting entityData`)
//   await store.dispatch(`entityDialog/setEntityData`, entityData, {
//     root: true,
//   })
// }

// if (entityParentObjectForCreate) {
//   console.debug(`${fn}  Setting parent`)
//   await store.dispatch(
//     `entityDialog/setEntityParentObjectForCreate`,
//     entityParentObjectForCreate,
//     {
//       root: true,
//     }
//   )
// }

// if (preRequestProcessor) {
//   console.debug(`${fn}  Setting preRequestProcessor`)
//   await store.dispatch(
//     `entityDialog/setPreRequestProcessor`,
//     preRequestProcessor,
//     {
//       root: true,
//     }
//   )
// }

// if (afterRequestCallback) {
//   console.debug(`${fn}  Setting afterRequestCallback`)
//   await store.dispatch(
//     `entityDialog/setAfterRequestCallback`,
//     afterRequestCallback,
//     {
//       root: true,
//     }
//   )
// }

// if (subentityNameTitleOverride) {
//   console.debug(`${fn}  Setting subentityNameTitleOverride`)
//   await store.dispatch(
//     `entityDialog/setSubentityNameTitleOverride`,
//     subentityNameTitleOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (subentityAfterRequestCallbacks) {
//   console.debug(`${fn}  Setting subentityAfterRequestCallbacks`)
//   await store.dispatch(
//     `entityDialog/setSubentityAfterRequestCallbacks`,
//     subentityAfterRequestCallbacks,
//     {
//       root: true,
//     }
//   )
// }

// if (subentityParentObjectForCreate) {
//   console.debug(`${fn}  Setting subentityParentObjectForCreate`)
//   await store.dispatch(
//     `entityDialog/setSubentityParentObjectForCreate`,
//     subentityParentObjectForCreate,
//     {
//       root: true,
//     }
//   )
// }

// if (storeEditedInputDataAsJsonInProperty) {
//   console.debug(`${fn}  Setting storeEditedInputDataAsJsonInProperty`)
//   await store.dispatch(
//     `entityDialog/setStoreEditedSpecsDataAsJsonInProperty`,
//     storeEditedInputDataAsJsonInProperty,
//     {
//       root: true,
//     }
//   )
// }

// if (titlePrefixOverride || titlePrefixOverride === '') {
//   console.debug(
//     `${fn}  Setting entityNameTitlePrefixOverride to: ${titlePrefixOverride}`
//   )
//   await store.dispatch(
//     `entityDialog/setEntityNameTitlePrefixOverride`,
//     titlePrefixOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (titleOverride) {
//   console.debug(`${fn}  Setting entityNameTitleOverride to: ${titleOverride}`)
//   await store.dispatch(
//     `entityDialog/setEntityNameTitleOverride`,
//     titleOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (subtitle) {
//   console.debug(`${fn}  Setting subtitle to: ${subtitle}`)
//   await store.dispatch(`entityDialog/setSubtitle`, subtitle, {
//     root: true,
//   })
// }

// if (message) {
//   console.debug(`${fn}  Setting message to: ${message}`)
//   await store.dispatch(`entityDialog/setMessage`, message, {
//     root: true,
//   })
// }

// if (saveButtonCaptionVerbOverride) {
//   console.debug(
//     `${fn}  Setting saveButtonCaptionOverride to: ${saveButtonCaptionVerbOverride}]`
//   )
//   await store.dispatch(
//     `entityDialog/setSaveButtonCaptionVerbOverride`,
//     saveButtonCaptionVerbOverride,
//     {
//       root: true,
//     }
//   )
// }

// if (deleteButtonCaptionsVerbOverride) {
//   console.debug(
//     `${fn}  Setting deleteButtonCaptionsOverride to: ${deleteButtonCaptionsVerbOverride}]`
//   )
//   await store.dispatch(
//     `entityDialog/setDeleteButtonCaptionsVerbOverride`,
//     deleteButtonCaptionsVerbOverride,
//     {
//       root: true,
//     }
//   )
// }

// const doAllowAddAnother = allowAddAnother ? allowAddAnother : false
// console.debug(`${fn}  Setting allowAddAnother to: ${doAllowAddAnother}`)
// await store.dispatch(`entityDialog/setAllowAddAnother`, doAllowAddAnother, {
//   root: true,
// })

// const doSartInViewMode = startInViewMode ? startInViewMode : false
// console.debug(`${fn}  Setting startInViewMode to: ${doSartInViewMode}`)
// await store.dispatch(`entityDialog/setStartInViewMode`, doSartInViewMode, {
//   root: true,
// })

// const doSuppressFocusOnFirstField = suppressFocusOnFirstField
//   ? suppressFocusOnFirstField
//   : false
// console.debug(
//   `${fn}  Setting suppressFocusOnFirstField to: ${doSuppressFocusOnFirstField}`
// )
// await store.dispatch(
//   `entityDialog/setSuppressFocusOnFirstField`,
//   doSuppressFocusOnFirstField,
//   {
//     root: true,
//   }
// )
